// page-meeting-header.ts — Entête de la fiche réunion : fil d'Ariane et bloc
// Informations (libellé, programme → projet, structure, comité, date, heure,
// risques), avec le volet repliable qui le contient.
//
// Extrait de `renderMeetingPage` le 2026-09-08 (point 16). Rendu pur à partir
// de `MeetingPageState` ; le câblage écrit dans ce même état et ne redessine
// que la liste des projets (cascade programme → projet, jamais l'inverse).
//
// Globaux ambiants : esc, t (core-globals.d.ts).

import type { MeetingPageState, TabKey } from '../domain/meeting-form.js';
import { renderStepsBlock } from './page-meeting-steps.js';
import { renderAgendaFieldsMount, renderGuidesSummaryMount } from './page-meeting-guides.js';

export interface HeaderProgram {
  id: number;
  code?: string | null;
  name: string;
}
export interface HeaderProject {
  id: number;
  code: string;
  name: string;
}
export interface HeaderCommittee {
  id: number;
  code: string;
  name: string;
}
export interface HeaderStructure {
  id: number;
  code: string;
  label: string;
}

export interface MeetingHeaderContext {
  meeting: { id: number; label: string } | null;
  state: MeetingPageState;
  programs: ReadonlyArray<HeaderProgram>;
  committees: ReadonlyArray<HeaderCommittee>;
  structures: ReadonlyArray<HeaderStructure>;
  /** Projets du programme sélectionné (tous si aucun programme). */
  visibleProjects: () => ReadonlyArray<HeaderProject>;
  /** Bloc en lecture seule : non-animateur, ou création sans droit. */
  infoDisabled: boolean;
  tabLabel: (k: TabKey) => string;
}

const COLLAPSE_KEY = 'meeting:infoCollapsed';

/** « #<id> — <libellé> » en édition, « Nouvelle réunion » en création. */
export function breadcrumbHtml(meeting: { id: number; label: string } | null): string {
  const current = meeting
    ? `#${meeting.id}${meeting.label && meeting.label.trim() ? ' — ' + meeting.label : ''}`
    : t('pageMeeting.newSuffix');
  return `<nav aria-label="${esc(t('pageMeeting.breadcrumbAria'))}" style="font-size:12px;color:var(--text2);margin-bottom:12px">
    <a href="#meetings-records" style="color:var(--accent);text-decoration:underline">${esc(t('pageMeetingsRecords.title'))}</a>
    <span style="color:var(--text3);margin:0 6px">›</span>
    <span style="color:var(--text2);font-weight:500">${esc(current)}</span>
  </nav>`;
}

/** Options du select projet : « Aucun » ou « Tous les projets du programme ». */
export function projectOptionsHtml(
  state: Pick<MeetingPageState, 'programId' | 'projectId'>,
  visibleProjects: () => ReadonlyArray<HeaderProject>,
): string {
  const includeAll = state.programId > 0;
  const allLabel = includeAll ? t('pageInviteCommittee.optAllProgramProjects') : t('common.none');
  return (
    `<option value="0">${esc(allLabel)}</option>` +
    visibleProjects()
      .map(
        (p) =>
          `<option value="${p.id}"${state.projectId === p.id ? ' selected' : ''}>${esc(p.code)} — ${esc(p.name)}</option>`,
      )
      .join('')
  );
}

function optionsHtml(
  items: ReadonlyArray<{ id: number; code: string | null | undefined; label: string }>,
  selectedId: number,
): string {
  return (
    `<option value="0">${esc(t('common.none'))}</option>` +
    items
      .map(
        (i) =>
          `<option value="${i.id}"${selectedId === i.id ? ' selected' : ''}>${esc(i.code || '')} — ${esc(i.label)}</option>`,
      )
      .join('')
  );
}

const byCode = <T extends { code?: string | null }>(a: T, b: T): number =>
  (a.code || '').localeCompare(b.code || '');

/** Fil d'Ariane + volet repliable (bloc Informations, déroulé, guides). */
export function meetingHeaderHtml(ctx: MeetingHeaderContext): string {
  const { state } = ctx;
  const lockedAttr = ctx.infoDisabled ? ' disabled' : '';
  const lockedTitle = ctx.infoDisabled ? ` title="${esc(t('common.accessDenied'))}"` : '';
  const programOptsHtml = optionsHtml(
    ctx.programs.map((p) => ({ id: p.id, code: p.code, label: p.name })),
    state.programId,
  );
  const committeeOptsHtml = optionsHtml(
    ctx.committees
      .slice()
      .sort(byCode)
      .map((c) => ({ id: c.id, code: c.code, label: c.name })),
    state.committeeId,
  );
  const structureOptsHtml = optionsHtml(
    ctx.structures
      .slice()
      .sort(byCode)
      .map((s) => ({ id: s.id, code: s.code, label: s.label })),
    state.structureId,
  );
  const infoBlockHtml = `
    <div class="card">
      <h2 style="font-size:15px;margin:0 0 12px 0">${esc(t('pageMeeting.blockInfo'))}</h2>
      <div class="form-row">
        <div class="form-group" style="flex:0 0 100%">
          <label>${esc(t('pageMeetingsRecords.fieldLabel'))}</label>
          <input type="text" id="mp-label" value="${esc(state.label)}"
            placeholder="${esc(t('pageMeetingsRecords.fieldLabel'))}"${lockedAttr}${lockedTitle}>
        </div>
      </div>
      <div class="form-row mt-3">
        <div class="form-group">
          <label>${esc(t('vocabulary.program'))}</label>
          <select id="mp-program"${lockedAttr}${lockedTitle}>${programOptsHtml}</select>
        </div>
        <div class="form-group">
          <label>${esc(t('pageMeetingsRecords.fieldProject'))}</label>
          <select id="mp-project"${lockedAttr}${lockedTitle}>${projectOptionsHtml(state, ctx.visibleProjects)}</select>
        </div>
        <div class="form-group">
          <label>${esc(t('pageMeetingsRecords.fieldStructure'))}</label>
          <select id="mp-structure"${lockedAttr}${lockedTitle}>${structureOptsHtml}</select>
        </div>
      </div>
      <div class="form-row mt-3">
        <div class="form-group">
          <label>${esc(t('pageMeetingsRecords.fieldCommittee'))}</label>
          <select id="mp-committee"${lockedAttr}${lockedTitle}>${committeeOptsHtml}</select>
        </div>
        <div class="form-group">
          <label>${esc(t('pageMeetingsRecords.fieldDate'))}</label>
          <input type="date" id="mp-date" data-testid="meeting-date" value="${esc(state.meetingDate)}"${lockedAttr}${lockedTitle}>
        </div>
        <div class="form-group">
          <label>${esc(t('pageMeetingsRecords.fieldTime'))}</label>
          <input type="time" id="mp-time" value="${esc(state.meetingTime)}"${lockedAttr}${lockedTitle}>
        </div>
      </div>
      <div class="form-row mt-3">
        <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer;padding:4px 0">
          <input type="checkbox" id="mp-with-risks" ${state.withRisks ? 'checked' : ''}${lockedAttr}>
          <span>${esc(t('pageMeeting.includeRisks'))}</span>
        </label>
      </div>
      ${renderAgendaFieldsMount()}
    </div>`;
  // Volet repliable : une fois la réunion identifiée, l'utilisateur gagne de
  // la place pour les onglets en repliant le bloc.
  return `${breadcrumbHtml(ctx.meeting)}
    <div id="mp-info-collapse" style="display:flex;flex-direction:column;gap:0">
      <button type="button" id="mp-info-toggle"
        aria-expanded="true"
        aria-controls="mp-info-body"
        style="display:flex;align-items:center;gap:6px;background:transparent;border:none;color:var(--text2);font-size:12px;cursor:pointer;padding:2px 0;align-self:flex-start">
        <span id="mp-info-toggle-chevron" style="display:inline-block;transition:transform .15s ease">▾</span>
        <span id="mp-info-toggle-label">${esc(t('pageMeeting.collapseHide'))}</span>
      </button>
      <div id="mp-info-body" style="display:grid;grid-template-columns:minmax(0,2fr) minmax(0,1fr) minmax(0,1fr);gap:14px;align-items:start;margin-top:6px">
        ${infoBlockHtml}
        ${renderStepsBlock(ctx.tabLabel)}
        ${renderGuidesSummaryMount()}
      </div>
    </div>`;
}

function applyCollapseState(collapsed: boolean): void {
  const body = document.getElementById('mp-info-body');
  const chev = document.getElementById('mp-info-toggle-chevron');
  const label = document.getElementById('mp-info-toggle-label');
  const btn = document.getElementById('mp-info-toggle');
  if (body) body.style.display = collapsed ? 'none' : 'grid';
  if (chev) chev.style.transform = collapsed ? 'rotate(-90deg)' : 'rotate(0deg)';
  if (label)
    label.textContent = collapsed ? t('pageMeeting.collapseShow') : t('pageMeeting.collapseHide');
  btn?.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
}

/**
 * Câble les champs du bloc Informations sur `state` et le volet repliable.
 * L'état du volet est mémorisé en sessionStorage : un switch d'onglet ne
 * redessine pas l'entête, mais un rechargement oui.
 */
export function wireMeetingHeader(
  state: MeetingPageState,
  visibleProjects: () => ReadonlyArray<HeaderProject>,
): void {
  const programSel = document.getElementById('mp-program') as HTMLSelectElement | null;
  const projectSel = document.getElementById('mp-project') as HTMLSelectElement | null;
  programSel?.addEventListener('change', () => {
    state.programId = parseInt(programSel.value) || 0;
    if (state.projectId && !visibleProjects().some((p) => p.id === state.projectId)) {
      state.projectId = 0;
    }
    if (projectSel) projectSel.innerHTML = projectOptionsHtml(state, visibleProjects);
  });
  projectSel?.addEventListener('change', () => {
    state.projectId = parseInt(projectSel.value) || 0;
  });
  document.getElementById('mp-structure')?.addEventListener('change', (e) => {
    state.structureId = parseInt((e.target as HTMLSelectElement).value) || 0;
  });
  document.getElementById('mp-committee')?.addEventListener('change', (e) => {
    state.committeeId = parseInt((e.target as HTMLSelectElement).value) || 0;
  });
  document.getElementById('mp-date')?.addEventListener('change', (e) => {
    state.meetingDate = (e.target as HTMLInputElement).value;
  });
  document.getElementById('mp-time')?.addEventListener('change', (e) => {
    state.meetingTime = (e.target as HTMLInputElement).value;
  });
  document.getElementById('mp-label')?.addEventListener('input', (e) => {
    state.label = (e.target as HTMLInputElement).value;
  });
  document.getElementById('mp-with-risks')?.addEventListener('change', (e) => {
    state.withRisks = !!(e.target as HTMLInputElement).checked;
  });

  let collapsed = false;
  try {
    collapsed = sessionStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    /* sessionStorage indisponible : on reste déplié */
  }
  if (collapsed) applyCollapseState(true);
  document.getElementById('mp-info-toggle')?.addEventListener('click', () => {
    collapsed = !collapsed;
    applyCollapseState(collapsed);
    try {
      sessionStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  });
}
